// Signed-in store account (coins, owned pids, favorites, history) shared by
// the product pages, the library and the account section. Loaded once per
// sign-in through `store {library}` and refreshed after every
// change (favorites, purchases).
import { useEffect, useState } from "react"
import PubSub from "pubsub-js"
import Value, { getValue } from "aldoo-ra/Value"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import { showErrorToast, showSuccessToast, showWarningToast } from "aldoo-ra/Toast"
import { STORE_ACCOUNT_KEY, STORE_ACCOUNT_PERSIST, SetFavorite, refreshStoreAccount } from "../storeApi"
import { setValue } from "aldoo-ra/Value"
import { trackAddToWishlist } from "../tracking"
import { makeT, getTable } from "../hooks/useT"

// "nobody yet", which is not the same as "no one signed in": a guest still
// has to be asked about once, or nothing would ever settle for them
let loadedFor = Symbol("unasked")
// Whether the account call has answered at least once this session. It is not
// the same as "signed in": an account can belong to the device rather than to
// a signed-in user, so nothing can be concluded about ownership before the
// first answer arrives.
let settled = false
const SETTLED_TOPIC = "sf-store-account-settled"

/**
 * The credentials as the browser holds them, read straight from storage.
 * PlatformAuth publishes them a tick after mounting and the shared value is
 * empty until it does, which is exactly the window in which a page would
 * decide it was serving a guest.
 */
function storedCredentials() {
  if (typeof window === "undefined") return null
  for (const key of [PlatformAuth.persistKey, "user-credentials"]) {
    if (!key) continue
    try {
      const raw = window.localStorage.getItem(key)
      const parsed = raw ? JSON.parse(raw) : null
      if (parsed?.auth_token) return parsed
    } catch {
      // unreadable storage is the same as no credentials
    }
  }
  return null
}

/**
 * The store account of the signed-in user (null for guests / until loaded).
 *
 * `resolved` says whether the answer is known: a guest is known at once, and
 * a signed-in reader is known as soon as the remembered account is read from
 * the browser — which is before the first paint on any visit after the first.
 * A page that decides what to show by ownership waits for it rather than
 * guessing and correcting itself.
 */
export function useStoreAccount() {
  const [auth] = PlatformAuth.useAuth()
  const [account] = Value({ key: STORE_ACCOUNT_KEY, defaultValue: null, persistKey: STORE_ACCOUNT_PERSIST })
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  const [, bump] = useState(0)
  useEffect(() => {
    const token = PubSub.subscribe(SETTLED_TOPIC, () => bump((n) => n + 1))
    return () => PubSub.unsubscribe(token)
  }, [])

  useEffect(() => {
    // credentials are in the browser but PlatformAuth has not published them
    // yet: asking now would ask as a guest and answer "owns nothing"
    if (!auth?.auth_token && storedCredentials()) return
    const identity = auth?.auth_token ? auth._id || auth.email : null
    if (loadedFor === identity) return
    loadedFor = identity
    refreshStoreAccount().finally(() => {
      settled = true
      PubSub.publish(SETTLED_TOPIC)
    })
  }, [auth?.auth_token, auth?._id])

  // The account may be the device's rather than a signed-in user's, so being
  // signed out proves nothing about ownership; only the answer does. The
  // remembered account makes that instant on every visit after the first.
  const signedIn = !!(auth?.auth_token || (mounted && storedCredentials()))

  return {
    account: mounted ? account : null,
    // Known: the account is in hand (remembered from the last visit, or just
    // fetched), or there is nobody to fetch one for. A reader whose
    // credentials are stored is never called a guest while their account is
    // still on its way — that was the "Buy" that turned into "Listen".
    resolved: mounted && (!!account || settled),
    refresh: refreshStoreAccount,
    auth: mounted ? auth : undefined,
  }
}

// the api refused this browser's session (PlatformAuth session-refused): what
// the account decided — owned titles, favorites — goes with it, and the
// reader is told why the page just signed them out
if (typeof window !== "undefined") {
  window.addEventListener(PlatformAuth.SESSION_REFUSED_EVENT, () => {
    setValue(STORE_ACCOUNT_KEY, null, STORE_ACCOUNT_PERSIST)
    // the question is answered for the guest this reader now is: nobody to
    // fetch an account for, so nothing stays "pending"
    loadedFor = null
    settled = true
    PubSub.publish(SETTLED_TOPIC)
    const t = makeT(getTable(getValue("current-site-language") || "en"))
    showWarningToast(t`You were signed out`, t`This device was signed out. Please sign in again.`, { duration: 6000, enableDismissalTracking: false })
  })
}

/**
 * "Added to favorites" / "Removed from favorites", with the product's name.
 * Every heart ends up in toggle(), so the confirmation is said once, for all
 * of them. Outside React (t() is a hook): the table of the current site
 * language is read directly, as the cart's toast does (store/cart.js).
 */
function favoriteToast(product, on) {
  const t = makeT(getTable(getValue("current-site-language") || "en"))
  showSuccessToast(on ? t`Added to favorites` : t`Removed from favorites`, product?.name || "", {
    duration: 2500,
    enableDismissalTracking: false,
  })
}

/** Favorite state + toggle for one product; persisted with store {favorites}. */
export function useFavorite(product) {
  const { account, auth } = useStoreAccount()
  const pid = product?.pid
  const isFavorite = !!(pid && account?.favorites?.includes(pid))

  const toggle = async () => {
    if (!pid) return false
    const next = !isFavorite
    try {
      const result = await SetFavorite(pid, next)
      if (result?.error && result.error !== "already_exists" && result.error !== "not_found") {
        showErrorToast(result.error)
        return isFavorite
      }
    } catch (error) {
      showErrorToast(error?.message || "Request failed")
      return isFavorite
    }
    if (next) trackAddToWishlist(product)
    favoriteToast(product, next)
    await refreshStoreAccount()
    PubSub.publish("sf-favorites-changed", { pid, on: next })
    return next
  }

  return { isFavorite, toggle, signedIn: !!auth?.auth_token }
}

export const ownsProduct = (account, pid) => !!(pid && account?.products?.includes(pid))
